import { createContext, useContext, type ReactNode } from 'react';

import type { StorefrontProduct } from '@/features/storefront/api/storefront';
import type { PartPresentation } from '@/features/storefront/parts/style';
import type { AnchorSectionType, PagePart, PartType } from '@/features/storefront/sections/parts';
import type { SectionDevice } from '@/features/storefront/sections/primitives';

/**
 * Everything a page's components need beyond their own settings.
 *
 * The same shape as `SectionEnvironment` one level down, and for the same
 * reason: one renderer, two callers. The shopper's storefront hands over a real
 * product and a real basket; the customiser hands over the merchant's own
 * catalog and a stand-in for the rest. Neither has to reimplement a part to
 * show it, which is what keeps the preview honest.
 *
 * Two ways a part gets its content, and which one a part uses is decided by
 * whether the page or the registry knows how to draw it:
 *
 * - **`slots`** - the page hands over a finished node. This is right for
 *   everything whose markup is entangled with the page's own logic: a checkout
 *   form with live validation, a catalog grid with its queries and pagination,
 *   a facet rail. The merchant still orders it, hides it, moves it between
 *   regions and paints it; they just do not get inside it.
 * - **the registry** - the part draws itself from the data here. This is the
 *   product page, where "the title", "the price" and "the buy button" really
 *   are separate pieces with separate settings, and where taking them apart is
 *   the whole point of the feature.
 */
export interface PartsEnvironment {
  /** Which page these parts belong to. Decides the regions and the library. */
  anchor: AnchorSectionType;
  parts: PagePart[];
  /** What this theme says about how its parts look. See `PartPresentation`. */
  presentation?: PartPresentation;
  /** Set by the customiser, whose device frame is narrower than its window. */
  device?: SectionDevice;
  /** True in the customiser, where a hidden part is dimmed rather than dropped. */
  includeHidden?: boolean;
  /** Finished nodes for the parts the page draws itself. */
  slots?: Partial<Record<PartType, ReactNode>>;
  /**
   * What an empty region says, by region name.
   *
   * Set by the customiser and nowhere else. A theme's product view asks for a
   * gallery column and a buy column without knowing that a merchant may have
   * emptied one, and on the shopper's page that is right - an empty column is
   * simply not drawn. In the editor it reads as a page that failed to load, so
   * the customiser hands over a line of dashed text per column and the theme's
   * own markup keeps drawing the columns it always drew.
   */
  emptyRegions?: Record<string, ReactNode>;
  /**
   * Wraps each part, for the customiser's selection outline. Left off on the
   * shopper's page, which has nothing to select.
   */
  wrap?: (part: PagePart, rendered: ReactNode) => ReactNode;
  /** The product being shown, on the one page that has one. */
  product?: ProductPartsData;
}

/**
 * The product page's own state, shared by the parts that make it up.
 *
 * The quantity lives here rather than inside the quantity part because the
 * button reads what the box says: two components, one number, and the page that
 * owns the mutation is the only thing that can hold it for both. A page with no
 * quantity box leaves it at one, which is what every theme sent before there
 * was a box to type in.
 */
export interface ProductPartsData {
  detail: StorefrontProduct;
  selected: StorefrontProduct['variants'][number] | undefined;
  onSelectVariant: (id: string) => void;
  onAddToCart: (quantity?: number) => void;
  onBuyNow: (quantity?: number) => void;
  adding: boolean;
  quantity: number;
  setQuantity: (next: number) => void;
}

export const PartsContext = createContext<PartsEnvironment | null>(null);

/**
 * The environment a part is being drawn in.
 *
 * Null outside a provider, which is a real state rather than a bug: a theme's
 * product view may be rendered by a test, or by a preview that has no document
 * behind it, and the right answer there is "render the page the way it always
 * rendered" rather than an exception.
 */
export function usePartsEnvironment(): PartsEnvironment | null {
  return useContext(PartsContext);
}

/**
 * The product a part is describing.
 *
 * Throwing would be wrong for the same reason: a product part outside a product
 * page is a document that got copied somewhere odd, and the page should lose
 * one component rather than white-screen.
 */
export function useProductParts(): ProductPartsData | null {
  return useContext(PartsContext)?.product ?? null;
}
